import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Steps, Table, message } from 'antd';
import { createServicePackage, getV1ServicePackagePreview, listV1ServicePackages } from '../../api/ownerReporting';
import { loadUnitsForProperty } from '../../api/hostListings';
import ServicePackageFormFields from './ServicePackageFormFields';
import {
  buildServicePackagePayload,
  assignedUnitsById,
  findDuplicateUnitIds,
  formValuesFromPreview,
  makeEmptyForm,
  payoutMethodLabel,
  groupingTypeLabel,
  syncUnitShareRates,
  validateServicePackageForm,
} from './formShared';
import styles from './CloneFromV1Wizard.module.css';

const wizardWidth = () => {
  if (typeof window === 'undefined') return 720;
  const available = window.innerWidth - 32;
  if (available < 520) return available;
  if (available < 992) return Math.min(720, available);
  return Math.min(800, available);
};

const packageTypeLabel = value => (value === 'tenancy-agreement' ? 'Fixed Rental' : 'Profit Sharing');
const shareTypeLabel = value => (value === 'share-by-revenue' ? 'Share by Revenue' : 'Share by Profit');

const DuplicateUnitWarning = ({ unitIds, unitNameById }) => {
  const ids = unitIds || [];
  if (!ids.length) return null;
  const names = ids.map(id => (unitNameById && unitNameById[String(id)]) || String(id));
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
      message="These units already belong to another service package"
      description={`${names.join(', ')}. They are unavailable and cannot be selected again.`}
    />
  );
};

const CloneFromV1Wizard = ({
  visible,
  hostId,
  existingPackages,
  properties,
  propertiesLoading,
  hostUnits,
  unitNameById,
  rememberUnitNames,
  onClose,
  onCreated,
}) => {
  const [step, setStep] = useState(0);
  const [v1Rows, setV1Rows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(undefined);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [v1Meta, setV1Meta] = useState(null);
  const [values, setValues] = useState(makeEmptyForm);
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalWidth, setModalWidth] = useState(wizardWidth());

  const reset = () => {
    setStep(0);
    setSelectedId(undefined);
    setV1Meta(null);
    setValues(makeEmptyForm());
    setUnits([]);
    setSaving(false);
    setPreviewLoading(false);
  };

  useEffect(() => {
    const onResize = () => setModalWidth(wizardWidth());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!visible || !hostId) return undefined;
    let cancelled = false;
    setListLoading(true);
    listV1ServicePackages(hostId)
      .then(rows => {
        if (cancelled) return;
        setV1Rows(rows || []);
        rememberUnitNames(
          (rows || []).reduce((acc, row) => acc.concat(row.units || []), []).map(u => ({
            _id: u._id,
            name: u.name,
          }))
        );
      })
      .catch(() => {
        if (cancelled) return;
        message.error('Failed to load v1 service packages');
        setV1Rows([]);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, hostId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!values.propertyId) {
      setUnits([]);
      return undefined;
    }
    let cancelled = false;
    setUnitsLoading(true);

    const fromHost = (hostUnits || []).filter(u => {
      const property = u.roomType && u.roomType.property;
      const propertyId = property && property._id ? property._id : property;
      return String(propertyId) === String(values.propertyId);
    });

    if (fromHost.length) {
      setUnits(fromHost);
      rememberUnitNames(fromHost);
      setUnitsLoading(false);
      return undefined;
    }

    loadUnitsForProperty(values.propertyId)
      .then(list => {
        if (cancelled) return;
        setUnits(list || []);
        rememberUnitNames(list || []);
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [values.propertyId, hostUnits]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = key => val => setValues(prev => Object.assign({}, prev, { [key]: val }));

  const onPropertyChange = propertyId => {
    setValues(prev => Object.assign({}, prev, { propertyId: propertyId ? String(propertyId) : undefined }));
  };

  const onUnitsChange = selected => {
    setValues(prev => {
      const currentPropertyUnitIds = {};
      (units || []).forEach(u => {
        currentPropertyUnitIds[String(u._id)] = true;
      });
      const kept = (prev.units || []).filter(id => !currentPropertyUnitIds[String(id)]);
      const merged = [];
      const seen = {};
      const assigned = assignedUnitsById(existingPackages);
      kept.concat((selected || []).map(String)).forEach(id => {
        if (seen[id] || assigned[id]) return;
        seen[id] = true;
        merged.push(id);
      });
      return Object.assign({}, prev, {
        units: merged,
        unitShareRates: prev.isGroupPackage ? syncUnitShareRates(merged, prev.unitShareRates) : prev.unitShareRates,
      });
    });
  };

  const onGroupPackageChange = checked => {
    setValues(prev =>
      Object.assign({}, prev, {
        isGroupPackage: checked,
        unitShareRates: checked ? syncUnitShareRates(prev.units || [], prev.unitShareRates) : [],
      })
    );
  };

  const onUnitSharePercentageChange = (unitId, ownerSharePercentage) => {
    setValues(prev =>
      Object.assign({}, prev, {
        unitShareRates: (prev.unitShareRates || []).map(item =>
          String(item.unitId) === String(unitId)
            ? Object.assign({}, item, { ownerSharePercentage: ownerSharePercentage == null ? 0 : Number(ownerSharePercentage) })
            : item
        ),
      })
    );
  };

  const assignedById = assignedUnitsById(existingPackages);
  const duplicateUnitIds = findDuplicateUnitIds(
    ((v1Meta && v1Meta.preview && v1Meta.preview.units) || []).map(id => (id && id._id ? id._id : id)),
    existingPackages
  );
  const selectedUnitsForCurrentProperty = (values.units || []).filter(id => units.some(u => String(u._id) === String(id)));

  const handleClose = () => {
    reset();
    onClose();
  };

  const goNext = async () => {
    if (step === 0) {
      if (!selectedId) {
        message.error('Select a v1 package');
        return;
      }
      setPreviewLoading(true);
      try {
        const item = await getV1ServicePackagePreview(hostId, selectedId);
        const preview = (item && item.preview) || {};
        setV1Meta(item);
        const next = formValuesFromPreview(preview, hostUnits);
        const assigned = assignedUnitsById(existingPackages);
        next.units = (next.units || []).filter(id => !assigned[String(id)]);
        if (next.isGroupPackage) {
          next.unitShareRates = syncUnitShareRates(next.units, next.unitShareRates);
        }
        setValues(next);
        rememberUnitNames(item && item.units ? item.units : []);
        setStep(1);
      } catch (e) {
        message.error((e && e.message) || 'Failed to load v1 package');
      } finally {
        setPreviewLoading(false);
      }
      return;
    }
    if (step === 1) {
      const error = validateServicePackageForm(values, assignedById);
      if (error) {
        message.error(error);
        return;
      }
      setStep(2);
    }
  };

  const goBack = () => {
    if (step <= 0) return;
    setStep(step - 1);
  };

  const onCreate = async () => {
    const error = validateServicePackageForm(values, assignedById);
    if (error) {
      message.error(error);
      return;
    }
    if (!hostId) {
      message.error('Select a host first');
      return;
    }
    setSaving(true);
    try {
      await createServicePackage(hostId, buildServicePackagePayload(values));
      message.success('Saved');
      reset();
      onCreated();
    } catch (e) {
      message.error((e && e.message) || 'Save failed');
      setSaving(false);
    }
  };

  const footer = [
    <Button key="cancel" onClick={handleClose}>
      Cancel
    </Button>,
  ];
  if (step > 0) {
    footer.push(
      <Button key="back" onClick={goBack}>
        Back
      </Button>
    );
  }
  if (step < 2) {
    footer.push(
      <Button key="next" type="primary" disabled={step === 0 && !selectedId} loading={previewLoading} onClick={goNext}>
        Next
      </Button>
    );
  } else {
    footer.push(
      <Button key="create" type="primary" loading={saving} onClick={onCreate}>
        Create
      </Button>
    );
  }

  return (
    <Modal
      visible={visible}
      title="Clone from v1"
      onCancel={handleClose}
      footer={footer}
      width={modalWidth}
      wrapClassName={styles.wrap}
      style={{ top: 24 }}
      destroyOnClose
    >
      <Steps current={step} size="small" className={styles.steps}>
        <Steps.Step title="Select v1 package" />
        <Steps.Step title="Edit package" />
        <Steps.Step title="Confirm" />
      </Steps>
      {step === 0 && (
        <div className={styles.tableWrap}>
          <Table
            rowKey="_id"
            size="small"
            loading={listLoading}
            dataSource={v1Rows}
            pagination={{ pageSize: 8, size: 'small' }}
            scroll={{ x: 480 }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedId ? [selectedId] : [],
              onChange: keys => setSelectedId(keys && keys[0]),
            }}
            onRow={record => ({
              onClick: () => setSelectedId(record._id),
            })}
            columns={[
              { title: 'Name', dataIndex: 'name', width: 160 },
              { title: 'Type', dataIndex: 'v1PackageType', width: 110 },
              {
                title: 'Units',
                render: r => {
                  const unitsForRow = r.units || [];
                  if (!unitsForRow.length) return 0;
                  const names = unitsForRow.map(u => u.name || u._id).slice(0, 3);
                  const more = unitsForRow.length > 3 ? ` +${unitsForRow.length - 3}` : '';
                  return `${names.join(', ')}${more}`;
                },
              },
              {
                title: 'Owner %',
                dataIndex: 'ownerSharePercentage',
                width: 88,
                align: 'right',
                render: value => (value == null ? '' : `${value}%`),
              },
            ]}
            locale={{ emptyText: 'No v1 service packages for this host' }}
          />
        </div>
      )}
      {step === 1 && (
        <div>
          <DuplicateUnitWarning unitIds={duplicateUnitIds} unitNameById={unitNameById} />
          {v1Meta && (
            <div style={{ marginBottom: 12, color: 'rgba(0,0,0,0.45)' }}>
              Mapped from v1 {v1Meta.v1PackageType} package. Package type defaults to Profit Sharing; you can switch to
              Group or Fixed Rental below.
            </div>
          )}
          <ServicePackageFormFields
            values={values}
            set={set}
            properties={properties}
            propertiesLoading={propertiesLoading}
            units={units}
            unitsLoading={unitsLoading}
            selectedUnitsForCurrentProperty={selectedUnitsForCurrentProperty}
            unitNameById={unitNameById}
            onPropertyChange={onPropertyChange}
            onUnitsChange={onUnitsChange}
            onGroupPackageChange={onGroupPackageChange}
            onUnitSharePercentageChange={onUnitSharePercentageChange}
            assignedById={assignedById}
          />
        </div>
      )}
      {step === 2 && (
        <div>
          <DuplicateUnitWarning unitIds={duplicateUnitIds} unitNameById={unitNameById} />
          <p>
            <strong>Name:</strong> {values.name}
          </p>
          <p>
            <strong>v1 type:</strong> {(v1Meta && v1Meta.v1PackageType) || '—'} → {groupingTypeLabel(values.groupingType)}
            {values.isGroupPackage ? ' (Group)' : ''}
          </p>
          <p>
            <strong>Agreement type:</strong> {packageTypeLabel(values.packageType)}
          </p>
          <p>
            <strong>Share type:</strong> {shareTypeLabel(values.shareType)}
          </p>
          <p>
            <strong>Owner share:</strong>{' '}
            {values.isGroupPackage ? 'Per unit' : `${values.ownerSharePercentage}%`}
          </p>
          <p>
            <strong>Allocate by SQFT:</strong> {values.shareProfitBySqft ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>Payout method:</strong> {payoutMethodLabel(values.payoutCalcMethod)}
          </p>
          <p>
            <strong>Payout start day:</strong> {values.startDay}
          </p>
          <p>
            <strong>Hide payout calendar:</strong> {values.hidePayout ? 'Yes' : 'No'}
          </p>
          <p>
            <strong>Units:</strong>{' '}
            {(values.units || []).length
              ? (values.units || []).map(id => (unitNameById && unitNameById[String(id)]) || String(id)).join(', ')
              : 'None'}
          </p>
        </div>
      )}
    </Modal>
  );
};

export default CloneFromV1Wizard;
